import type { WorkoutSession } from './types';
import { sessionSets, sessionVolume, sessionXp } from './training';

export const dayKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
export function mondayOf(date: Date) {
  const monday = new Date(date);
  monday.setHours(0,0,0,0);
  monday.setDate(monday.getDate() - (monday.getDay()+6)%7);
  return monday;
}
export function rewards(sessions: WorkoutSession[], now = new Date()) {
  // Only finished sessions with actual logged sets count. IDs are deduplicated for restored backups.
  const completed = [...new Map(sessions.filter(s => s.status === 'completed' && s.finishedAt
    && Number.isFinite(Date.parse(s.startedAt)) && Date.parse(s.startedAt) <= now.getTime()
    && Date.parse(s.finishedAt) >= Date.parse(s.startedAt) && Date.parse(s.finishedAt) <= now.getTime()
    && sessionSets(s).length).map(s => [s.id,s])).values()];
  const weekly = new Map<string, { count: number; sets: number }>();
  for (const s of completed) {
    const key = dayKey(mondayOf(new Date(s.startedAt)));
    const week = weekly.get(key) ?? { count: 0, sets: 0 };
    weekly.set(key, { count: week.count+1, sets: week.sets+sessionSets(s).length });
  }
  const weekKey = dayKey(mondayOf(now));
  const week = weekly.get(weekKey) ?? { count: 0, sets: 0 };
  // A quest is awarded once for every qualifying calendar week. It never disappears on Monday.
  const questXp = [...weekly.values()].filter(w => w.sets >= 20).length * 80;
  const xp = completed.reduce((sum,s) => sum+sessionXp(s),0)+questXp;
  const keys = new Set(completed.map(s => dayKey(new Date(s.startedAt))));
  const dates = [...keys].sort();
  let longestStreak = 0, run = 0;
  for (const key of dates) {
    const previous = new Date(`${key}T12:00:00`); previous.setDate(previous.getDate()-1);
    run = keys.has(dayKey(previous)) ? run+1 : 1;
    longestStreak = Math.max(longestStreak, run);
  }
  let streak = 0;
  const cursor = new Date(now);
  if (!keys.has(dayKey(cursor))) cursor.setDate(cursor.getDate()-1);
  while(keys.has(dayKey(cursor))) { streak++; cursor.setDate(cursor.getDate()-1); }
  const weekSessions = completed.filter(s => dayKey(mondayOf(new Date(s.startedAt))) === weekKey);
  const totalSets = completed.reduce((sum,s) => sum+sessionSets(s).length,0);
  const volume = completed.reduce((sum,s) => sum+sessionVolume(s),0);
  const bestWeek = Math.max(0, ...[...weekly.values()].map(w => w.count));
  const badges = [
    { id: 'first', name: 'Langkah pertama', description: '1 sesi dengan set tercatat', goal: 1, value: completed.length, icon: 'spark' },
    { id: 'week', name: 'Temukan ritmemu', description: '3 sesi dalam satu minggu', goal: 3, value: bestWeek, icon: 'calendar' },
    { id: 'streak', name: 'Konsisten', description: 'Pernah latihan 3 hari berurutan', goal: 3, value: longestStreak, icon: 'flame' },
    { id: 'sets', name: 'Lima puluh set', description: '50 set selesai sepanjang waktu', goal: 50, value: totalSets, icon: 'dumbbell' },
    { id: 'volume', name: 'Angkat seribu', description: '1.000 kg volume beban × repetisi', goal: 1000, value: volume, icon: 'trophy' },
  ].map(b => ({...b, earned: b.value >= b.goal}));
  const days = Array.from({length:7},(_,i) => {
    const date = mondayOf(now); date.setDate(date.getDate()+i);
    const items = weekSessions.filter(s => dayKey(new Date(s.startedAt)) === dayKey(date));
    return { key: dayKey(date), date, label: date.toLocaleDateString('id-ID',{weekday:'short'}), count: items.length,
      sets: items.reduce((sum,s) => sum+sessionSets(s).length,0), today: dayKey(date) === dayKey(now) };
  });
  return { completed, xp, questXp, level: Math.floor(xp/500)+1, levelXp: xp%500, streak, longestStreak, totalSets, volume, badges, days,
    weekSessions: week.count, weekSets: week.sets,
    weekVolume: weekSessions.reduce((sum,s) => sum+sessionVolume(s),0), questComplete: week.sets >= 20 };
}
